/** Biblioteca VISUAL de plantillas — miniatura/imagen, nombre, familia, descripción y receta vinculada.
 *  Una imagen NO crea recetas ni cálculos: es solo referencia visual ligada a una receta/familia existente. */
import React, { useState, useEffect, useRef } from 'react';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trash, PencilSimple, Plus, UploadSimple } from '@phosphor-icons/react';
import { PreviewDiseno } from './Disenador2D';

const FAMS = [
  { v: 'ventanas', label: 'Ventanas', receta: false },
  { v: 'puertas', label: 'Puertas', receta: false },
  { v: 'pabm', label: 'Puerta de baño (PABM)', receta: true },
  { v: 'vtf', label: 'Vidrio fijo ducha (VTF)', receta: true },
];
const recetaDe = (fam) => FAMS.find((f) => f.v === fam)?.receta
  ? `Receta ${String(fam).toUpperCase()} vinculada`
  : 'Receta no vinculada (solo visual / diseño 2D)';

export const PlantillasAdmin = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nueva, setNueva] = useState(null); // {nombre, familia, descripcion}
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const fotoRef = useRef(null);
  const [fotoId, setFotoId] = useState(null);

  const load = () => {
    api.get('/sa/plantillas').then((r) => setItems(r.data.plantillas || [])).catch(() => toast.error('Error al cargar plantillas')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const guardarNueva = async () => {
    if (!nueva.nombre.trim()) return toast.error('Ponle nombre a la plantilla');
    setSaving(true);
    try {
      const { data } = await api.post('/sa/plantillas', { nombre: nueva.nombre.trim(), familia: nueva.familia, descripcion: nueva.descripcion || '', diseno: {}, variables: {} });
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/sa/plantillas/${data.id}/imagen`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`Plantilla visual "${nueva.nombre}" guardada — ${recetaDe(nueva.familia)}`);
      setNueva(null); setFile(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };

  const subirFoto = async (f) => {
    if (!f || !fotoId) return;
    const fd = new FormData();
    fd.append('file', f);
    try { await api.post(`/sa/plantillas/${fotoId}/imagen`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Imagen guardada'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo subir'); }
    setFotoId(null);
  };

  const renombrar = async (t) => {
    const nombre = window.prompt('Nuevo nombre de la plantilla:', t.nombre);
    if (!nombre || nombre === t.nombre) return;
    try { await api.put(`/sa/plantillas/${t.id}`, { nombre }); toast.success('Plantilla renombrada'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo renombrar'); }
  };

  const eliminar = async (t) => {
    if (!window.confirm(`¿Eliminar la plantilla "${t.nombre}"?`)) return;
    try { await api.delete(`/sa/plantillas/${t.id}`); toast.success('Plantilla eliminada'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo eliminar'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Biblioteca de plantillas</DialogTitle></DialogHeader>
        <input ref={fotoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { subirFoto(e.target.files?.[0]); e.target.value = ''; }} data-testid="plantilla-foto-input" />
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-[var(--text-muted)] flex-1">
            Para usarlas: dentro de un presupuesto elige "Usar plantilla…" en el elemento. Las nuevas desde el diseñador se guardan con "Guardar como plantilla".
          </p>
          <Button size="sm" className="h-8 text-xs bg-[#0EA5E9] hover:bg-[#0284C7] text-white shrink-0" onClick={() => setNueva({ nombre: '', familia: 'ventanas', descripcion: '' })} data-testid="plantilla-agregar-btn">
            <Plus size={13} className="mr-1" /> Agregar plantilla
          </Button>
        </div>

        {nueva && (
          <div className="border border-dashed border-[#0EA5E9] rounded-lg p-2.5 space-y-1.5" data-testid="plantilla-nueva-form">
            <p className="text-[10px] font-black uppercase text-[#0EA5E9]">Nueva plantilla visual — la imagen/plano NO crea recetas ni cálculos</p>
            <div className="flex flex-wrap gap-1.5">
              <Input placeholder="Nombre (ej. Fijo + puerta + fijo)" value={nueva.nombre} onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })} className="h-8 text-xs w-52" data-testid="plantilla-nueva-nombre" />
              <select value={nueva.familia} onChange={(e) => setNueva({ ...nueva, familia: e.target.value })}
                className="h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="plantilla-nueva-familia">
                {FAMS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
              <Input placeholder="Descripción corta" value={nueva.descripcion} onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })} className="h-8 text-xs flex-1 min-w-[140px]" data-testid="plantilla-nueva-desc" />
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => fileRef.current?.click()} data-testid="plantilla-nueva-imagen">
                <UploadSimple size={13} className="mr-1" /> {file ? file.name.slice(0, 18) : 'Imagen / plano'}
              </Button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
            <p className={`text-[10px] font-bold ${FAMS.find((f) => f.v === nueva.familia)?.receta ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="plantilla-receta-status">
              {recetaDe(nueva.familia)}
            </p>
            <div className="flex gap-1.5">
              <Button size="sm" className="h-8 text-xs bg-[#0EA5E9] text-white" onClick={guardarNueva} disabled={saving} data-testid="plantilla-nueva-guardar">{saving ? 'Guardando…' : 'Guardar plantilla'}</Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setNueva(null); setFile(null); }}>Cancelar</Button>
            </div>
          </div>
        )}

        {loading ? <p className="text-sm text-[var(--text-muted)]">Cargando…</p> : items.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] text-center py-6">Sin plantillas.</p>
        ) : (
          FAMS.filter((f) => items.some((t) => t.familia === f.v)).map((f) => (
            <div key={f.v} className="space-y-1.5">
              <p className="text-xs font-black uppercase text-[#0EA5E9]" data-testid={`plantillas-fam-${f.v}`}>{f.label} ({items.filter((t) => t.familia === f.v).length})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.filter((t) => t.familia === f.v).map((t) => (
              <div key={t.id} className="border border-[var(--border)] rounded-lg p-2.5 flex gap-2" data-testid={`plantilla-item-${t.id}`}>
                {t.imagen_path
                  ? <img src={`${api.defaults.baseURL}/sa/catalogo/imagen/${t.imagen_path}`} alt={t.nombre} className="w-[100px] h-[90px] object-contain border border-[var(--border)] rounded bg-white" data-testid={`plantilla-img-${t.id}`} />
                  : t.diseno?.tipo === 'composicion'
                    ? <div className="w-[100px] h-[90px] border border-[var(--border)] rounded flex items-center justify-center text-2xl">🧱</div>
                    : <PreviewDiseno diseno={t.diseno} height={90} />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[var(--text)] leading-tight">{t.nombre}</p>
                  <p className="text-[11px] text-[var(--text-muted)] capitalize">{t.familia}{t.seed ? ' · inicial' : ''}</p>
                  {t.descripcion && <p className="text-[10px] text-[var(--text-muted)] italic">{t.descripcion}</p>}
                  <p className={`text-[10px] font-bold ${FAMS.find((f) => f.v === t.familia)?.receta ? 'text-emerald-600' : 'text-amber-600'}`}>{recetaDe(t.familia)}</p>
                  <div className="flex gap-1 mt-1">
                    <Button variant="ghost" size="sm" className="h-6 px-1.5" title="Renombrar" onClick={() => renombrar(t)} data-testid={`plantilla-ren-${t.id}`}><PencilSimple size={13} /></Button>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5" title="Subir imagen/plano" onClick={() => { setFotoId(t.id); fotoRef.current?.click(); }} data-testid={`plantilla-foto-${t.id}`}>📷</Button>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-red-400" title="Eliminar" onClick={() => eliminar(t)} data-testid={`plantilla-del-${t.id}`}><Trash size={13} /></Button>
                  </div>
                </div>
              </div>
                ))}
              </div>
            </div>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};
